import { useRef, useState } from 'react'
import { ChevronDown, Gamepad2, Joystick } from 'lucide-react'
import { STUFEN, type Stufe } from './useHandsteuerung'
import type { StickWerte } from './useGamepad'

// Bedienelemente der Handsteuerung: Wisch-Sticks über dem Bild, Mini-Kreuze mit den gesendeten Werten, Panel für die
// Bedienleiste (Einschalten, Tempo-Stufe, Gamepad-Anzeige, Latenz, Hilfe). Die Logik liegt in useHandsteuerung.

const STICK_GROESSE = 144   // Durchmesser in px (Tippfläche weit über 48 px)

/** Ein Touch-Stick: springt beim Loslassen in die Mitte. Zwei Sticks lassen sich gleichzeitig bedienen (je eigener Pointer). */
function WischStick({ beschriftung, onWert }: { beschriftung: string; onWert: (x: number, y: number) => void }) {
  const feld = useRef<HTMLDivElement | null>(null)
  const zeiger = useRef<number | null>(null)
  const [punkt, setPunkt] = useState({ x: 0, y: 0 })

  const lesen = (e: React.PointerEvent) => {
    const r = feld.current!.getBoundingClientRect()
    let dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
    let dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
    const l = Math.hypot(dx, dy)
    if (l > 1) { dx /= l; dy /= l }
    setPunkt({ x: dx, y: dy })
    onWert(Math.abs(dx) < 0.08 ? 0 : dx, Math.abs(dy) < 0.08 ? 0 : -dy)   // Bildschirm-y nach unten -> Stick nach oben positiv
  }
  const loslassen = (e: React.PointerEvent) => {
    if (zeiger.current !== e.pointerId) return
    zeiger.current = null
    setPunkt({ x: 0, y: 0 }); onWert(0, 0)
  }
  return (
    <div ref={feld} role="application" aria-label={beschriftung} data-stick={beschriftung}
      onPointerDown={e => { if (zeiger.current !== null) return; zeiger.current = e.pointerId; e.currentTarget.setPointerCapture(e.pointerId); lesen(e) }}
      onPointerMove={e => { if (zeiger.current === e.pointerId) lesen(e) }}
      onPointerUp={loslassen} onPointerCancel={loslassen} onLostPointerCapture={loslassen}
      style={{ touchAction: 'none', width: STICK_GROESSE, height: STICK_GROESSE }}
      className="pointer-events-auto relative select-none rounded-full border-2 border-white/40 bg-black/25 backdrop-blur-[1px]">
      <span className="pointer-events-none absolute inset-x-0 -top-5 text-center text-[10px] text-white/70">{beschriftung}</span>
      <span className="pointer-events-none absolute left-1/2 top-1/2 h-12 w-12 rounded-full bg-primary-500/85 shadow"
        style={{ transform: `translate(calc(-50% + ${punkt.x * (STICK_GROESSE / 2 - 24)}px), calc(-50% + ${punkt.y * (STICK_GROESSE / 2 - 24)}px))` }} />
    </div>
  )
}

/** Zwei Wisch-Sticks unten links/rechts im Bild (Modus 2): links steigen/sinken + drehen, rechts vor/zurück + seitlich. */
export function TouchSticks({ onWert }: { onWert: (seite: 'links' | 'rechts', x: number, y: number) => void }) {
  return (
    <div className="pointer-events-none absolute inset-x-4 bottom-4 z-[34] flex items-end justify-between">
      <WischStick beschriftung="Höhe / Drehen" onWert={(x, y) => onWert('links', x, y)} />
      <WischStick beschriftung="Vor / Seitlich" onWert={(x, y) => onWert('rechts', x, y)} />
    </div>
  )
}

/** Mini-Kreuz: zeigt, was gerade gesendet wird (Punkt in der Mitte = 0). */
function MiniKreuz({ x, y, titel }: { x: number; y: number; titel: string }) {
  return (
    <div className="space-y-0.5 text-center">
      <div className="relative mx-auto h-14 w-14 rounded border border-white/25 bg-black/30" aria-label={`${titel}: ${x.toFixed(2)} / ${y.toFixed(2)}`}>
        <span className="absolute left-1/2 top-0 h-full w-px bg-white/15" /><span className="absolute left-0 top-1/2 h-px w-full bg-white/15" />
        <span className={`absolute h-3 w-3 rounded-full ${x === 0 && y === 0 ? 'bg-white/50' : 'bg-primary-500'}`}
          style={{ left: `calc(50% + ${x * 22}px - 6px)`, top: `calc(50% - ${y * 22}px - 6px)` }} />
      </div>
      <div className="text-[10px] text-white/55">{titel}</div>
    </div>
  )
}

export function HandsteuerungPanel({ an, sperrTitel, stufe, werte, fehler, gamepad, latenzMs, socketOffen, verzoegerungMs, touchSticks, onTouchSticks, onEin, onAus, onStufe }: {
  an: boolean
  /** Grund der Sperre (gesetzt = Knopf gesperrt) */
  sperrTitel: string | undefined
  stufe: Stufe
  werte: StickWerte
  fehler: string | null
  gamepad: { verbunden: boolean; name: string | null }
  latenzMs: number | null | undefined
  socketOffen: boolean
  verzoegerungMs: number | null
  touchSticks: boolean
  onTouchSticks: (v: boolean) => void
  onEin: () => void
  onAus: () => void
  onStufe: (s: Stufe) => void
}) {
  const [hilfe, setHilfe] = useState(false)
  const langsam = verzoegerungMs !== null && verzoegerungMs > 3000
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-dark-text-subtle">Handsteuerung</div>
      <button type="button" onClick={an ? onAus : onEin} disabled={!an && !!sperrTitel} aria-pressed={an}
        title={an ? 'Handsteuerung ausschalten' : sperrTitel ?? 'Handsteuerung einschalten – Bewegung nur bei gehaltener Taste/Stick'}
        className={`flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-lg border px-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          an ? 'border-amber-400 bg-amber-400 text-black' : 'border-dark-border bg-dark-bg-tertiary text-dark-text hover:border-primary-500/60'}`}>
        <Joystick className="h-4 w-4" /> {an ? 'Handsteuerung AUS' : 'Handsteuerung ein'}
      </button>
      {!an && sperrTitel && <p className="text-[11px] text-white/50">{sperrTitel}</p>}

      <div className="grid grid-cols-3 gap-1.5" role="group" aria-label="Tempo-Stufe">
        {STUFEN.map(s => (
          <button key={s} type="button" onClick={() => onStufe(s)} aria-pressed={stufe === s}
            title={`Höchstgeschwindigkeit ${s} m/s bei vollem Ausschlag`}
            className={`min-h-[48px] rounded-lg border px-1 text-sm font-semibold ${stufe === s ? 'border-primary-500 bg-primary-500 text-white' : 'border-dark-border bg-dark-bg-tertiary text-dark-text'}`}>
            {s} m/s
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-white/70">
        <Gamepad2 className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate" title={gamepad.name ?? undefined}>{gamepad.verbunden ? `Gamepad erkannt: ${gamepad.name}` : 'Kein Gamepad (Taste am Controller drücken)'}</span>
      </div>
      <label className="flex min-h-[48px] items-center gap-2 text-sm">
        <input type="checkbox" checked={touchSticks} onChange={e => onTouchSticks(e.target.checked)} className="h-5 w-5" />
        Wisch-Sticks im Bild
      </label>

      {an && (
        <div className="space-y-1.5 rounded-lg border border-amber-400/40 bg-amber-400/5 p-2">
          <div className="flex items-center justify-between text-xs">
            <span className={socketOffen ? 'text-emerald-300' : 'text-amber-300'}>{socketOffen ? 'Verbindung steht' : 'Verbindung wird aufgebaut …'}</span>
            <span className="font-mono tabular-nums text-white/70">DRC {latenzMs !== null && latenzMs !== undefined ? `${latenzMs} ms` : '– ms'}</span>
          </div>
          <div className="flex justify-around">
            <MiniKreuz x={werte.r} y={werte.z} titel="Höhe / Drehen" />
            <MiniKreuz x={werte.x} y={werte.y} titel="Vor / Seitlich" />
          </div>
          {langsam && <p className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white">Bild verzögert – nur langsam steuern</p>}
        </div>
      )}
      {fehler && <p role="alert" className="rounded-lg bg-red-600 px-2.5 py-2 text-sm font-semibold text-white">{fehler}</p>}

      <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary">
        <button type="button" onClick={() => setHilfe(h => !h)} aria-expanded={hilfe}
          className="flex min-h-[48px] w-full items-center justify-between gap-2 px-3 text-sm font-semibold text-dark-text">
          Belegung &amp; Hinweise <ChevronDown className={`h-4 w-4 transition-transform ${hilfe ? 'rotate-180' : ''}`} />
        </button>
        {hilfe && (
          <div className="space-y-2 border-t border-white/10 p-3 text-xs leading-snug text-white/75">
            <div><b className="text-white">Notstopp</b> (immer, sobald die Steuerung erteilt ist): roter Knopf im Bild, Leertaste, Gamepad B/Kreis. Die Drohne hält an und schwebt.</div>
            <div><b className="text-white">Tastatur</b> (nicht in Eingabefeldern): W/S vor/zurück · A/D seitlich · Q/E drehen links/rechts · C steigen · Z sinken.</div>
            <div><b className="text-white">Gamepad</b> (Modus 2): linker Stick hoch/runter = steigen/sinken, links/rechts = drehen; rechter Stick = vor/zurück und seitlich. Totzone 10 %.</div>
            <div><b className="text-white">Wisch-Sticks</b>: gleiche Belegung, springen beim Loslassen in die Mitte; beide Daumen gleichzeitig möglich.</div>
            <div><b className="text-white">Tempo</b>: 1 / 3 / 5 m/s bei vollem Ausschlag. Bewegung nur, solange Stick oder Taste gehalten wird.</div>
            <div>Die Handsteuerung schaltet sich selbst aus bei Seitenwechsel, Tab im Hintergrund, Fensterfokus-Verlust, Verbindungsfehler, Gamepad-Trennung sowie bei FlyTo/POI/Rückkehr.</div>
            <div className="rounded bg-amber-400/15 px-2 py-1 text-amber-100">Der Pilot bleibt verantwortlich und kann die Steuerung jederzeit an der Fernsteuerung zurücknehmen.</div>
          </div>
        )}
      </div>
    </div>
  )
}
